// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-SCENE-QUERY — the `entities` route body (PR-A, behind the ABB-S6 seam)
// ═══════════════════════════════════════════════════════════════════════════
//
// The `entities` route projector: the per-entity metadata page, PAGINATED by the offsetKey
// cursor, SORTED by a `key:dir` grammar, FIELD-projected by `?fields`, SCOPED by the seam's
// resolved space. A BODY only — the envelope / cursor shape / `?scope` resolution / value
// validation (`unknown-sort` / `unknown-field`) all live in the canonical `sceneSummaryProjector`
// seam; this fn is registered via `registerRouteProjector('entities', projectEntities)` (the
// `ai/sceneQuery/index.ts` entry point S7 calls at mount). Mirrors `projectSelection`'s structure.
//
// The pagination / sort-parse / fullField-single-field-mode / field-projection PRIMITIVES live in
// the shared `projectionHelpers.ts` (PR-B extraction) — this body and the 3 table-route bodies
// (`projectLayers`/`projectBlocks`/`projectSpaces`) reuse them so the page shape is one truth
// (dup:check). This body is the original; PR-B refactored it to import the helpers (no behaviour
// change — the `entities` matrix tests still pass).
//
// LEAF purity (ПРИОРИТЕТ #2): imports ONLY the seam + the shared helpers — NO editorStore /
// agentBridge / command-engine. `npm run deps:graph` (depcruise) cycles=0.
//
// THE #1 LESSON FROM ABB-S6 (INV-9): `routeRevisions` binds the ROUTE-SPECIFIC subset
// (`geometryRevision` ONLY for entities — the entity STATE the page projects), NEVER the coarse
// `snapshotRevision` (it DUAL-bumps on every commit → would FALSELY stale EVERY continuation
// after any unrelated mutation). `snapshotRevision` rides as the `page.snapshotRevision` ANCHOR.
// ═══════════════════════════════════════════════════════════════════════════

import { type RouteProjector, type SnapshotEntityRef } from "../sceneSummaryProjector";
import {
  compareFacetsBy,
  paginate,
  parseKeyDirSort,
  projectFacetFields,
  resolveProjectionMode,
} from "./projectionHelpers";

/**
 * The LEAN CORE field set projected when `?fields` is ABSENT (byte economy — the default page
 * stays small). A `?fields=` request widens / narrows to exactly the requested allowlisted keys.
 * Every member is in `ROUTE_QUERY_ALLOWLISTS.entities.fields` (INV-1 — never emit a field the
 * allowlist does not advertise).
 */
const LEAN_CORE_FIELDS = [
  "id",
  "type",
  "layer",
  "spaceId",
  "color",
  "linetype",
  "lineweight",
] as const satisfies readonly (keyof SnapshotEntityRef)[];

/** The legal `key:dir` sort KEYS for the entities route (the canonical explicit grammar). */
const ENTITY_SORT_KEYS = ["type", "layer", "id"] as const;
type EntitySortKey = (typeof ENTITY_SORT_KEYS)[number];

/**
 * The `entities` route body. Scope → sort → (fullField mode | field-project) → paginate. Binds
 * `routeRevisions` to `geometryRevision` ONLY (the entity STATE this route projects);
 * `snapshotRevision` rides as the page ANCHOR (NEVER a cursor-binding key — INV-9, the #1 ABB-S6
 * lesson). `byteTruncated: false` (the companion serializer owns truncation against the per-request
 * byte budget).
 *
 * `?fullField` (stateRoute.ts:98-102 / fullstack:955/967 — "single-field UNCAPPED full-value
 * retrieval, forces `limit=1` + field-chunk cursor") takes PRECEDENCE over `?fields`/lean-core (per
 * @codex: "handle `fullField` before falling back to `fields`"). It FORCES one item per page
 * (limit=1, overriding `?limit`) and projects ONLY `{[fullField]: <full value>}`. For ENTITIES the
 * field-chunk cursor is VACUOUS — every entity metadata field is an atomic small scalar / short
 * string / small color object, none exceeding the per-request frame budget — so a single offsetKey
 * page (one whole item) is the complete value (the resolveProjectionMode helper forces limit=1).
 */
export const projectEntities: RouteProjector = ({ query, cursor, snapshot, resolvedSpaceId }) => {
  // SCOPE — the seam already resolved `?scope` (and neutralized an unknown scope to `entities:[]`).
  // `null` → all in-scope entities (document scope); else filter to the resolved space.
  const scoped =
    resolvedSpaceId === null
      ? snapshot.entities
      : snapshot.entities.filter((e) => e.spaceId === resolvedSpaceId);

  // SORT (before pagination — INV-cursor-determinism). A COPY so the snapshot ref is not mutated;
  // a stable comparator preserves snapshot order on a tie. No `?sort` → the natural snapshot order.
  // `type`/`layer`/`id` are all STRING values in the model (entity ids are `z.string()`).
  const parsedSort = parseKeyDirSort<EntitySortKey>(query.sort, ENTITY_SORT_KEYS);
  const ordered =
    parsedSort === null
      ? scoped
      : [...scoped].sort((a, b) =>
          compareFacetsBy(a, b, parsedSort.key, parsedSort.dir, (e, k) => e[k]),
        );

  // PROJECTION-MODE + LIMIT. `?fullField` (the seam already validated it is an allowlisted field)
  // takes PRECEDENCE over `?fields`/lean-core and FORCES limit=1 (one whole item per page — a
  // single-field UNCAPPED page). Else the normal `?fields`/lean-core page, clamped by `?limit`.
  const { fields, limit } = resolveProjectionMode(query, LEAN_CORE_FIELDS);

  // PAGINATION — the SHARED paginate primitive (offset decode + slice + nextOffsetKey).
  const { slice, nextOffsetKey } = paginate(ordered, cursor, limit);

  // FIELD PROJECTION (INV-1 — only allowlisted fields). An OPTIONAL field absent on the entity is
  // omitted (the key, not `undefined`) — so a `?fullField` whose value is absent yields an empty `{}`.
  const items = slice.map((e) => projectFacetFields(e as unknown as Record<string, unknown>, fields));

  // @codex T1 arm(c) round-10 P2 finding: `entityBounds` (T1 arm (c)) resolves a `Block`
  // entity's bounds through `blockDefinitions` — the document's TABLE axis (`tableRevision`
  // bumps on "block-DEFINITION authoring", `editorStoreRevisionDiff.ts::docHeaderChanged`'s own
  // doc), NOT the entity STATE axis (`geometryRevision`) this route otherwise binds to. Without
  // this, a paged `entities?fields=entityBounds` read that straddles a block-definition edit
  // (BEDIT save, WBLOCK-redefine) between two `nextOffsetKey` pages would NOT get a stale-cursor
  // rejection, silently mixing bounds computed against two different block tables within one
  // logical page walk. Bound ONLY when `entityBounds` is actually requested (`fields` — lean-core
  // never includes it) so the overwhelmingly common non-bounds page keeps the narrower, more
  // stable `geometryRevision`-only binding (fewer false-stale continuations after an unrelated
  // table edit, e.g. a layer-colour change, that this page's ACTUAL projected values never read).
  const needsTableRevision = fields.includes("entityBounds");
  // Built via if/else into an EXPLICITLY-typed const (not an inline ternary): a ternary between
  // two differently-shaped object literals infers a unified type that marks the branch lacking
  // `tableRevision` as `tableRevision?: undefined` — incompatible with `Record<string, number>`'s
  // index signature (`undefined` is not `number`) regardless of `exactOptionalPropertyTypes`.
  const routeRevisions: Readonly<Record<string, number>> = needsTableRevision
    ? { geometryRevision: snapshot.revisions.geometryRevision, tableRevision: snapshot.revisions.tableRevision }
    : { geometryRevision: snapshot.revisions.geometryRevision };

  return {
    items,
    nextOffsetKey,
    // ROUTE-SPECIFIC binding tuple ([L7] / INV-9): the entity page depends on entity STATE
    // (geometryRevision — add/erase/move/grip/property edit), PLUS the table axis
    // (tableRevision) when `entityBounds` is requested (see above). `snapshotRevision` is
    // DELIBERATELY EXCLUDED (it bumps on EVERY commit → would false-stale a continuation after
    // any unrelated mutation, e.g. a selection change); it rides as the `page.snapshotRevision`
    // ANCHOR only.
    routeRevisions,
    resolvedSpaceId,
    snapshotRevision: snapshot.snapshotRevision, // RESPONSE ANCHOR (not a cursor-binding key).
    byteTruncated: false, // the companion serializer owns byte-budget truncation.
    projectionVersion: snapshot.projectionVersion,
    total: ordered.length,
  };
};
